import React from "react";
import { MdClose } from "react-icons/md";
import type { UserInviteCreate, UserRole, CenterListItem } from "types/domain";
import { centerService } from "services/admin/centerService";
import { userService } from "services/admin/userService";
import ActionButton from "components/ui/ActionButton";
import { useLanguage } from "context/LanguageContext";

interface CreateUserModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: UserInviteCreate) => Promise<void>;
  currentUserRole?: UserRole | null;
  presetCenterId?: string;
}

const DEFAULT_PASSWORD = "12345678";

const INITIAL_FORM: UserInviteCreate = {
  full_name: "",
  email: "",
  role: "TRAINER",
  password: DEFAULT_PASSWORD,
  send_email: false,
  phone: "",
  username: "",
  center_id: undefined,
  skip_first_login: true,
};

// Labels/descriptions are translation keys, resolved with t() at render.
const BASE_ROLE_OPTIONS: Array<{
  value: UserRole;
  label: string;
  description: string;
}> = [
  {
    value: "TRAINER",
    label: "usersMgmt.create.roleTrainerLabel",
    description: "usersMgmt.create.roleTrainerDesc",
  },
  {
    value: "TEACHER",
    label: "enum.role.TEACHER",
    description: "usersMgmt.create.roleTeacherDesc",
  },
  {
    value: "STUDENT",
    label: "enum.role.STUDENT",
    description: "usersMgmt.create.roleStudentDesc",
  },
];

const MANAGER_OPTION = {
  value: "MANAGER" as UserRole,
  label: "enum.role.MANAGER",
  description: "usersMgmt.create.roleManagerDesc",
};

function CreateUserModal({
  isOpen,
  onClose,
  onSubmit,
  currentUserRole,
  presetCenterId,
}: CreateUserModalProps) {
  const { t } = useLanguage();
  const [form, setForm] = React.useState<UserInviteCreate>(INITIAL_FORM);
  const [submitting, setSubmitting] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [showPassword, setShowPassword] = React.useState(false);
  const [centers, setCenters] = React.useState<CenterListItem[]>([]);
  const [centersLoading, setCentersLoading] = React.useState(false);
  const [usernameAutoFilled, setUsernameAutoFilled] = React.useState(false);
  const [usernameLoading, setUsernameLoading] = React.useState(false);

  const isSuperAdmin = currentUserRole === "SYSTEM_ADMIN";
  const isCenterScopedSystemAdmin = isSuperAdmin && Boolean(presetCenterId);

  // Fetch centers list for system admin
  React.useEffect(() => {
    if (!isOpen || !isSuperAdmin) return;
    setCentersLoading(true);
    centerService
      .getAll({ limit: 200 })
      .then((res) => setCenters(res.items))
      .catch(() => setCenters([]))
      .finally(() => setCentersLoading(false));
  }, [isOpen, isSuperAdmin]);

  React.useEffect(() => {
    if (!isOpen) return;
    if (!isCenterScopedSystemAdmin || !presetCenterId) return;
    setForm((prev) => ({ ...prev, center_id: presetCenterId }));
  }, [isCenterScopedSystemAdmin, isOpen, presetCenterId]);

  // Resolve the effective center ID for the next-username API call
  const effectiveCenterId = isSuperAdmin ? form.center_id : undefined;

  // Fetch next auto-generated username from API
  React.useEffect(() => {
    if (!isOpen) return;
    // Only auto-fill if user hasn't manually typed
    if (!usernameAutoFilled && form.username) return;

    setUsernameLoading(true);
    userService
      .getNextUsername(effectiveCenterId)
      .then((res) => {
        if (res.username) {
          setForm((prev) => ({ ...prev, username: res.username! }));
          setUsernameAutoFilled(true);
        } else if (usernameAutoFilled) {
          setForm((prev) => ({ ...prev, username: "" }));
          setUsernameAutoFilled(false);
        }
      })
      .catch(() => {})
      .finally(() => setUsernameLoading(false));
  }, [isOpen, effectiveCenterId]); // eslint-disable-line react-hooks/exhaustive-deps

  const roleOptions = React.useMemo(
    () =>
      isSuperAdmin ? [MANAGER_OPTION, ...BASE_ROLE_OPTIONS] : BASE_ROLE_OPTIONS,
    [isSuperAdmin]
  );

  if (!isOpen) return null;

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value, type } = e.target;
    if (type === "checkbox") {
      setForm((prev) => ({
        ...prev,
        [name]: (e.target as HTMLInputElement).checked,
      }));
    } else {
      setForm((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleRoleChange = (role: UserRole) => {
    setForm((prev) => ({ ...prev, role }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (isSuperAdmin && !form.center_id) {
      setError("usersMgmt.create.errorCenterRequired");
      return;
    }

    setSubmitting(true);
    try {
      const normalizeOptional = (value?: string) => {
        const trimmed = value?.trim();
        return trimmed ? trimmed : undefined;
      };

      await onSubmit({
        ...form,
        full_name: normalizeOptional(form.full_name),
        email: normalizeOptional(form.email),
        phone: form.phone?.trim() || undefined,
        username: normalizeOptional(form.username),
        password: normalizeOptional(form.password),
      });
      setForm(INITIAL_FORM);
      setUsernameAutoFilled(false);
      onClose();
    } catch (err: unknown) {
      setError(
        err instanceof Error ? err.message : "usersMgmt.create.errorCreate"
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleBackdrop = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) onClose();
  };

  return (
    <div
      className="bg-black/50 fixed inset-0 z-50 flex items-center justify-center px-4"
      onClick={handleBackdrop}
    >
      <div className="flex h-[calc(100vh-2rem)] w-full max-w-xl flex-col overflow-hidden rounded-2xl bg-surface shadow-2xl sm:h-auto sm:max-h-[90vh]">
        <div className="flex shrink-0 items-center justify-between border-b border-line px-6 py-4">
          <h3 className="text-lg font-bold text-ink">
            {t("usersMgmt.create.title")}
          </h3>
          <button
            type="button"
            onClick={onClose}
            aria-label={t("action.close")}
            className="cursor-pointer rounded-lg p-1.5 text-ink-subtle hover:bg-surface-hover hover:text-gray-600"
          >
            <MdClose className="h-5 w-5" />
          </button>
        </div>

        <form
          onSubmit={handleSubmit}
          className="flex-1 space-y-5 overflow-y-auto px-6 py-5"
        >
          {/* Role Selection */}
          <div>
            <label className="mb-1.5 block text-sm font-medium text-ink">
              {t("usersMgmt.create.role")}{" "}
              <span className="text-red-500">*</span>
            </label>
            <select
              name="role"
              value={form.role}
              onChange={(e) => handleRoleChange(e.target.value as UserRole)}
              className="w-full rounded-lg border border-line px-3 py-2 text-sm text-ink outline-none focus:border-brand-500 dark:bg-surface-muted"
            >
              {roleOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {t(option.label)}
                </option>
              ))}
            </select>
            <p className="mt-1 text-xs text-ink-muted">
              {t(
                roleOptions.find((item) => item.value === form.role)
                  ?.description ?? ""
              )}
            </p>
          </div>

          {/* Center Selection (System Admin only) */}
          {isSuperAdmin && (
            <div>
              <label className="mb-1.5 block text-sm font-medium text-ink">
                {t("usersMgmt.create.center")}{" "}
                <span className="text-red-500">*</span>
              </label>
              {centersLoading ? (
                <div className="h-10 w-full animate-pulse rounded-lg bg-gray-200 dark:bg-surface-muted" />
              ) : (
                <select
                  name="center_id"
                  value={form.center_id ?? ""}
                  onChange={handleChange}
                  disabled={isCenterScopedSystemAdmin}
                  required
                  className="w-full rounded-lg border border-line px-3 py-2 text-sm text-ink outline-none focus:border-brand-500 disabled:cursor-not-allowed disabled:bg-gray-100 dark:bg-surface-muted dark:disabled:bg-surface-muted/70"
                >
                  <option value="">{t("usersMgmt.create.selectCenter")}</option>
                  {centers.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
              )}
            </div>
          )}

          {/* Name Field */}
          <div>
            <label className="mb-1.5 block text-sm font-medium text-ink">
              {t("firstLogin.fullName")} <span className="text-red-500">*</span>
            </label>
            <input
              name="full_name"
              value={form.full_name ?? ""}
              onChange={handleChange}
              required
              placeholder={t("usersMgmt.create.fullNamePlaceholder")}
              className="w-full rounded-lg border border-line px-3 py-2 text-sm text-ink outline-none focus:border-brand-500 dark:bg-surface-muted dark:placeholder-gray-400"
            />
          </div>

          {/* Phone Field */}
          <div>
            <label className="mb-1.5 block text-sm font-medium text-ink">
              {t("settings.phone")}
            </label>
            <input
              name="phone"
              type="tel"
              value={form.phone}
              onChange={handleChange}
              placeholder="0901234567"
              className="w-full rounded-lg border border-line px-3 py-2 text-sm text-ink outline-none focus:border-brand-500 dark:bg-surface-muted dark:placeholder-gray-400"
            />
          </div>

          {/* Email and Username */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="mb-1.5 block text-sm font-medium text-ink">
                Email
              </label>
              <input
                name="email"
                type="email"
                value={form.email ?? ""}
                onChange={handleChange}
                placeholder="example@email.com"
                className="w-full rounded-lg border border-line px-3 py-2 text-sm text-ink outline-none focus:border-brand-500 dark:bg-surface-muted dark:placeholder-gray-400"
              />
            </div>
            <div>
              <label className="mb-1.5 block text-sm font-medium text-ink">
                {t("usersMgmt.create.username")}
              </label>
              <div className="relative">
                <input
                  name="username"
                  type="text"
                  value={form.username ?? ""}
                  onChange={(e) => {
                    setUsernameAutoFilled(false);
                    handleChange(e);
                  }}
                  placeholder={t("usersMgmt.create.optional")}
                  minLength={3}
                  maxLength={50}
                  className="w-full rounded-lg border border-line px-3 py-2 text-sm text-ink outline-none focus:border-brand-500 dark:bg-surface-muted dark:placeholder-gray-400"
                />
                {usernameLoading && (
                  <div className="absolute right-2 top-1/2 -translate-y-1/2">
                    <div className="border-t-transparent h-4 w-4 animate-spin rounded-full border-2 border-brand-500" />
                  </div>
                )}
              </div>
              {usernameAutoFilled && (
                <p className="mt-1 text-xs text-ink-subtle">
                  {t("usersMgmt.create.usernameAutoHint")}
                </p>
              )}
            </div>
          </div>

          {/* Password Field */}
          <div>
            <label className="mb-1.5 block text-sm font-medium text-ink">
              {t("usersMgmt.create.password")}
            </label>
            <div className="relative">
              <input
                name="password"
                type={showPassword ? "text" : "password"}
                value={form.password ?? ""}
                onChange={handleChange}
                placeholder={t("usersMgmt.create.passwordPlaceholder", {
                  password: DEFAULT_PASSWORD,
                })}
                minLength={8}
                className="w-full rounded-lg border border-line px-3 py-2 pr-16 text-sm text-ink outline-none focus:border-brand-500 dark:bg-surface-muted dark:placeholder-gray-400"
              />
              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                className="absolute right-2 top-1/2 -translate-y-1/2 rounded px-2 py-0.5 text-xs text-ink-muted hover:text-brand-500"
              >
                {showPassword
                  ? t("usersMgmt.create.hidePassword")
                  : t("usersMgmt.create.showPassword")}
              </button>
            </div>
            <p className="mt-1 text-xs text-ink-subtle">
              {t("usersMgmt.create.passwordHint")}
            </p>
          </div>

          {/* Send Email Checkbox */}
          <label className="flex cursor-pointer items-center gap-2">
            <input
              name="send_email"
              type="checkbox"
              checked={form.send_email ?? false}
              onChange={handleChange}
              className="h-4 w-4 rounded border-line text-brand-500 focus:ring-brand-500"
            />
            <span className="text-sm text-ink">
              {t("usersMgmt.create.sendEmail")}
            </span>
          </label>

          {/* Skip First Login Checkbox */}
          <label className="flex cursor-pointer items-center gap-2">
            <input
              name="skip_first_login"
              type="checkbox"
              checked={form.skip_first_login ?? false}
              onChange={handleChange}
              className="h-4 w-4 rounded border-line text-brand-500 focus:ring-brand-500"
            />
            <span className="text-sm text-ink">
              {t("usersMgmt.create.skipFirstLogin")}
            </span>
          </label>

          {/* Info Box */}
          <div className="rounded-xl border border-brand-100 bg-brand-50/70 px-4 py-3 text-sm text-brand-700 dark:border-brand-900/30 dark:bg-brand-900/10 dark:text-brand-300">
            {form.skip_first_login
              ? t("usersMgmt.create.infoSkipFirstLogin")
              : t("usersMgmt.create.infoFirstLogin")}
          </div>

          {/* Error Message */}
          {error && (
            <p role="alert" className="text-sm text-red-500">
              {t(error)}
            </p>
          )}

          {/* Action Buttons */}
          <div className="sticky bottom-0 flex items-center justify-end gap-3 border-t border-line bg-surface pb-1 pt-4">
            <ActionButton type="button" variant="secondary" onClick={onClose}>
              {t("action.cancel")}
            </ActionButton>
            <ActionButton type="submit" variant="primary" disabled={submitting}>
              {submitting
                ? t("usersMgmt.create.creating")
                : t("usersMgmt.create.submit")}
            </ActionButton>
          </div>
        </form>
      </div>
    </div>
  );
}

export default CreateUserModal;
